import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Download,
  FileSpreadsheet,
  Filter,
  CalendarIcon,
  FileDown,
  CheckSquare,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exportColumns = [
  { key: "lead_id", label: "Lead ID" },
  { key: "order_no", label: "Order No" },
  { key: "aged", label: "Aged" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "street", label: "Street" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP" },
  { key: "bank", label: "Bank" },
  { key: "sourceID", label: "Source ID" },
  { key: "source_name", label: "Source Name" },
  { key: "upload_date", label: "Upload Date" },
  { key: "is_duplicate", label: "Is Duplicate" },
  { key: "duplicate_type", label: "Duplicate Type" },
  { key: "created_at", label: "Created At" },
];

const ExportCenter = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ states: [], banks: [], sources: [] });

  // Export settings
  const [exportMode, setExportMode] = useState("all");
  const [exportType, setExportType] = useState("csv");
  const [selectedColumns, setSelectedColumns] = useState(
    exportColumns.map((c) => c.key)
  );

  // Filters
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [bank, setBank] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await fetch(`${API}/filters/options`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setFilterOptions(data);
        }
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      }
    };
    fetchFilterOptions();
  }, []);

  const toggleColumn = (columnKey) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey]
    );
  };

  const selectAllColumns = () => {
    setSelectedColumns(exportColumns.map((c) => c.key));
  };

  const deselectAllColumns = () => {
    setSelectedColumns([]);
  };

  const handleExport = async () => {
    if (user?.role === "Viewer") {
      toast.error("You don't have permission to export data");
      return;
    }

    if (selectedColumns.length === 0) {
      toast.error("Please select at least one column to export");
      return;
    }

    setLoading(true);

    try {
      const filters = {};
      if (state) filters.state = state;
      if (city) filters.city = city;
      if (bank) filters.bank = bank;
      if (sourceName) filters.source_name = sourceName;
      if (dateFrom) filters.date_from = format(dateFrom, "yyyy-MM-dd");
      if (dateTo) filters.date_to = format(dateTo, "yyyy-MM-dd");

      const response = await fetch(`${API}/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          filters,
          export_mode: exportMode,
          export_type: exportType,
          columns: selectedColumns,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const ext = exportType === "xlsx" ? "xlsx" : "csv";
        a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Export completed!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Export failed");
      }
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role === "Viewer") {
    return (
      <Layout title="Export Center" subtitle="Export your lead data">
        <Card className="border-border/60">
          <CardContent className="p-12 text-center">
            <Download className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Permission Required</h2>
            <p className="text-muted-foreground">
              You don't have permission to export data. Please contact an administrator.
            </p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Export Center" subtitle="Export your lead data">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Export Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Export Mode */}
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                <FileDown className="w-5 h-5" />
                Export Mode
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <Button
                  variant={exportMode === "all" ? "default" : "outline"}
                  onClick={() => setExportMode("all")}
                  className="h-auto py-4 flex-col"
                  data-testid="export-mode-all"
                >
                  <FileSpreadsheet className="w-6 h-6 mb-2" />
                  <span className="font-medium">All Leads</span>
                  <span className="text-xs opacity-70 mt-1">Include everything</span>
                </Button>
                <Button
                  variant={exportMode === "duplicates_only" ? "default" : "outline"}
                  onClick={() => setExportMode("duplicates_only")}
                  className="h-auto py-4 flex-col"
                  data-testid="export-mode-duplicates"
                >
                  <FileSpreadsheet className="w-6 h-6 mb-2" />
                  <span className="font-medium">Duplicates Only</span>
                  <span className="text-xs opacity-70 mt-1">Only duplicate records</span>
                </Button>
                <Button
                  variant={exportMode === "unique_only" ? "default" : "outline"}
                  onClick={() => setExportMode("unique_only")}
                  className="h-auto py-4 flex-col"
                  data-testid="export-mode-unique"
                >
                  <FileSpreadsheet className="w-6 h-6 mb-2" />
                  <span className="font-medium">Unique Only</span>
                  <span className="text-xs opacity-70 mt-1">Exclude duplicates</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    State
                  </Label>
                  <Select value={state || "all"} onValueChange={(v) => setState(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="export-filter-state">
                      <SelectValue placeholder="All states" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All states</SelectItem>
                      {filterOptions.states.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    City
                  </Label>
                  <Input
                    data-testid="export-filter-city"
                    placeholder="Enter city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Bank
                  </Label>
                  <Select value={bank || "all"} onValueChange={(v) => setBank(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="export-filter-bank">
                      <SelectValue placeholder="All banks" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All banks</SelectItem>
                      {filterOptions.banks.map((b) => (
                        <SelectItem key={b} value={b}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Source
                  </Label>
                  <Select value={sourceName || "all"} onValueChange={(v) => setSourceName(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="export-filter-source">
                      <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sources</SelectItem>
                      {filterOptions.sources.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date From
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="export-date-from"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date To
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="export-date-to"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Column Selection */}
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <CheckSquare className="w-5 h-5" />
                  Select Columns
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllColumns}
                    data-testid="select-all-columns"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deselectAllColumns}
                    data-testid="deselect-all-columns"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {exportColumns.map((col) => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={selectedColumns.includes(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                      data-testid={`col-${col.key}`}
                    />
                    <span className="text-sm">{col.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                {selectedColumns.length} of {exportColumns.length} columns selected
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Export Summary */}
        <div>
          <Card className="border-border/60 sticky top-24">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold font-['Manrope']">
                Export Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Export Mode
                </p>
                <p className="font-medium">
                  {exportMode === "all"
                    ? "All Leads"
                    : exportMode === "duplicates_only"
                    ? "Duplicates Only"
                    : "Unique Only"}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Format
                </p>
                <Select value={exportType} onValueChange={setExportType}>
                  <SelectTrigger data-testid="export-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Columns
                </p>
                <p className="font-medium">{selectedColumns.length} selected</p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Filters Applied
                </p>
                <p className="font-medium">
                  {[state, city, bank, sourceName, dateFrom, dateTo].filter(Boolean).length || "None"}
                </p>
              </div>

              <Button
                onClick={handleExport}
                disabled={loading || selectedColumns.length === 0}
                className="w-full active-scale"
                data-testid="export-btn"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export Now
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ExportCenter;
